import { Feature, FeatureCollection, Geometry } from "geojson";

import { DatasetDetailRow } from "@/components/datasetManagement/details/columns";
import { COLORS } from "@/constants/colors";
/**
 * Converts a DatasetResponse to a GeoJSON FeatureCollection.
 * Each row in the dataset becomes a feature, where the properties are mapped from the columns.
 * If attributesSchema is provided, it's used for mapping; otherwise, response.columns is used.
 */
import {
  CATCHMENT_GRID_ID_COLUMN_NAME,
  DATA_TYPE_VALUES,
  DATASET_ATTRIBUTE_SCHEMA_DEFAULT_VALUE,
  DEFAULT_STEP,
  ORDINAL_TYPES,
  ORDINAL_TYPES_COLUMN,
  SPATIAL_AGGREGATION_NAME
} from "@/constants/dataset";
import { Timezone } from "@/constants/dateTime";
import { normalizeFilterValue } from "@/lib/utils";
import { defaultDataset } from "@/mocks/apis/dataset/data";
import { SpatialAggregationType } from "@/types/common";
import {
  AddUpdateRowsDatasetPayload,
  ATTRIBUTE_ID,
  CreateNewDatasetType,
  DataSchemaType,
  Dataset,
  DatasetAttributeSchema,
  DatasetData,
  DatasetDataColumn,
  DatasetDataRowValue,
  DatasetDataType,
  DatasetMetadata,
  DatasetMetadataStream,
  DatasetsMap,
  EColorLabel,
  EColorNames,
  H3Configuration,
  TransformedProperty
} from "@/types/dataset";
import { SelectedFeature } from "@/types/map";
import { EOutputAnalysisType } from "@/types/spatial";

import { processValue } from "./attributeProcessing";
import { handleCategoricalValues, handleNumericValues } from "./common";
import { convertFormatterDayjs } from "./date";
import { formatRenderValue } from "./string";
import { isWKTPoint, wktToGeoJSON } from "./wkt";

interface ProcessedRowData {
  properties: Record<string, string | number | boolean | null | undefined>;
  baseId: number | string | undefined;
  collectedGeometries: Array<{
    geometry: Geometry;
    sourceColumnName: string;
    columnIndex: number;
  }>;
}

/**
 * Util function to check if the dataset data `rows` value is in v2 format.
 */

const processRowValue = (
  value: DatasetDataRowValue,
  columnInfo: DatasetAttributeSchema | DatasetDataColumn,
  useAttributesSchema: boolean,
  columnIndex: number,
  processedData: ProcessedRowData,
  spatialAggregationType?: SpatialAggregationType
) => {
  const columnName = useAttributesSchema
    ? (columnInfo as DatasetAttributeSchema).attribute_name
    : (columnInfo as DatasetDataColumn).name;

  const isReferenceId = useAttributesSchema
    ? (columnInfo as DatasetAttributeSchema).is_reference_id ||
      columnName === "id"
    : columnName === "id";

  const isGeometryColumn = useAttributesSchema
    ? (columnInfo as DatasetAttributeSchema).is_reference_geo
    : (columnInfo as DatasetDataColumn).data_type ===
      DatasetDataType.TYPE_GEOMETRY;

  if (isReferenceId) {
    let idValue: string | number | undefined;

    if (typeof value === "string" || typeof value === "number") {
      idValue = value;
    } else if (value !== null && value !== undefined) {
      idValue = String(idValue);
    }

    processedData.baseId = idValue ?? columnIndex.toString();
    processedData.properties[columnName] = processedData.baseId;
  } else if (isGeometryColumn && typeof value === "string" && value) {
    // Check if spatialAggregationType is 'poi' and validate geometry is a point
    if (spatialAggregationType === "poi" && !isWKTPoint(value)) {
      // Skip non-point geometries when spatial aggregation type is 'poi'
      return;
    }
    if (
      spatialAggregationType === SPATIAL_AGGREGATION_NAME.H3 ||
      spatialAggregationType === SPATIAL_AGGREGATION_NAME.geohash.toLowerCase()
    ) {
      processedData.properties[columnName] = value;
      return;
    }
    const geometry = wktToGeoJSON(value)?.geometry;
    if (geometry) {
      processedData.collectedGeometries.push({
        geometry,
        sourceColumnName: columnName,
        columnIndex
      });
    }
  } else if (!isGeometryColumn) {
    processValue(
      value,
      columnInfo as DatasetDataColumn,
      processedData.properties
    );
  }
};

const createFeaturesFromRowData = (
  processedData: ProcessedRowData,
  rowIndex: number,
  features: Array<Feature<Geometry>>
) => {
  const { baseId, collectedGeometries, properties } = processedData;

  if (collectedGeometries.length > 0) {
    collectedGeometries.forEach(
      ({ geometry, sourceColumnName, columnIndex }, geometryIndex) => {
        const featureId =
          baseId !== undefined
            ? `${baseId}-${geometryIndex}`
            : `${rowIndex}-${columnIndex}-${geometryIndex}`;

        features.push({
          type: "Feature",
          id: featureId,
          geometry: geometry,
          properties: {
            ...properties,
            ["Geometry Source"]: sourceColumnName
          }
        });
      }
    );
  } else {
    const featureId = baseId !== undefined ? baseId : rowIndex.toString();
    features.push({
      type: "Feature",
      id: featureId,
      geometry: null as unknown as Geometry,
      properties: properties
    });
  }
};

export const convertDatasetResponseToFeatureCollection = (
  datasetData: DatasetData,
  attributesSchema?: DatasetAttributeSchema[]
): FeatureCollection<Geometry> => {
  if (!datasetData) return { type: "FeatureCollection", features: [] };
  const { columns, rows, spatial_aggregation_type } = datasetData;
  const useAttributesSchema = !!attributesSchema;
  const features: Array<Feature<Geometry>> = [];

  rows.forEach((rowData, rowIndex) => {
    const processedData: ProcessedRowData = {
      properties: {},
      baseId: undefined,
      collectedGeometries: []
    };

    rowData.forEach((rowValue, columnIndex) => {
      const columnInfo = useAttributesSchema
        ? attributesSchema[columnIndex]
        : columns[columnIndex];

      processRowValue(
        rowValue,
        columnInfo,
        useAttributesSchema,
        columnIndex,
        processedData,
        spatial_aggregation_type
      );
    });

    createFeaturesFromRowData(processedData, rowIndex, features);
  });

  return {
    type: "FeatureCollection",
    features: features
  };
};

/**
 * Gets all unique property keys from feature properties
 */
export const getCategoriesFromDatasetReponse = (
  response: DatasetData
): {
  type: DatasetDataType;
  value: string;
}[] => {
  const categories: {
    type: DatasetDataType;
    value: string;
  }[] = [];

  response.columns.forEach((column) => {
    if (
      column.name === "geom" ||
      column.name === "id" ||
      CATCHMENT_GRID_ID_COLUMN_NAME.includes(column.name)
    )
      return;

    categories.push({
      type: column.data_type,
      value: column.name
    });
  });

  return categories;
};

/**
 * Calculates color distribution ranges for feature properties based on their type.
 * For string values, it creates color categories.
 * For numeric values, it creates quantile ranges.
 */
export const calculateFeaturePropertyColorRanges = (
  featureCollection: FeatureCollection<Geometry>,
  propertyName: string,
  isOrdinal: boolean,
  step: number
) => {
  // Extract property values
  const propertyValues = featureCollection.features.map(
    (f) => f.properties?.[propertyName]
  );

  return isOrdinal
    ? handleCategoricalValues(propertyValues, step)
    : handleNumericValues(propertyValues, step);
};

/*
 * Transforms properties of a selected feature into a more structured format
 * by associating them with their respective categories.
 * This is useful for displaying properties in a categorized manner.
 */
export const transformPropertiesWithCategory = (
  properties: SelectedFeature,
  categories: DatasetAttributeSchema[]
): TransformedProperty[] => {
  return Object.entries(properties).reduce<TransformedProperty[]>(
    (acc, [key, value]) => {
      const category = categories.find((cat) => cat.attribute_name === key);
      if (category) {
        acc.push({
          key,
          value,
          data_type: category.data_type
        });
      }
      return acc;
    },
    []
  );
};

/**
 * Transforms an array of properties into a key-value pair object.
 * Each property is formatted based on its data type using the `formatRenderValue` function.
 * This transformation is useful for efficiently accessing attributes by their keys
 * and displaying them in a structured format.
 */
export const transformVisibleAttributes = (
  transformProperty: TransformedProperty[],
  timezone?: Timezone
): Record<string, string> => {
  return transformProperty.reduce<Record<string, string>>((acc, item) => {
    const safeValue = item.value ?? "";
    const formattedValue = formatRenderValue(
      item.data_type,
      safeValue,
      timezone
    );
    acc[item.key] = String(formattedValue);
    return acc;
  }, {});
};

/** Function to return only the available/not deleted `datasets` from the given `datasets` slice */
export const getAvailableDatasets = (datasets: DatasetsMap): DatasetsMap => {
  return Object.fromEntries(
    Object.entries(datasets).filter(([, dataset]) => !!dataset.data.available)
  );
};

/**
 * Selects the most appropriate attribute to use as the default category for dataset visualization.
 *
 * The selection follows a priority order:
 * 1. Prefers the "score" attribute if it exists (case-insensitive)
 * 2. Otherwise, selects the first non-special, non-geometry attribute
 * 3. Falls back to the first attribute in the array
 *
 * @param attributes - Array of dataset attribute schemas to choose from
 * @returns The selected attribute schema that should be used as the default category
 *
 * @example
 * ```typescript
 * const attributes = [
 *   { attribute_name: 'id', data_type: DataSchemaType.STRING },
 *   { attribute_name: 'score', data_type: DataSchemaType.NUMERIC },
 *   { attribute_name: 'geometry', data_type: DataSchemaType.GEOMETRY }
 * ];
 *
 * const defaultCategory = getDefaultSelectedCategory(attributes);
 * // Returns the 'score' attribute
 * ```
 */
export const getDefaultSelectedCategory = (
  attributes: DatasetAttributeSchema[]
): DatasetAttributeSchema => {
  // 1. Prefer the "score" attribute if it exists
  const scoreAttribute = attributes.find(
    (attr) => attr.attribute_name?.toLowerCase() === "score"
  );
  if (scoreAttribute) return scoreAttribute;

  // 2. Otherwise, prefer the first non-special, non-geometry attribute
  const regularAttribute = attributes.find(
    (attr) =>
      !(attr.attribute_name in ATTRIBUTE_ID) &&
      attr.data_type !== DataSchemaType.GEOMETRY
  );
  if (regularAttribute) return regularAttribute;

  // 3. Fallback: return the first attribute
  return attributes[0];
};

/**
 * Creates a default spatial dataset object with configured properties, styles, and visibility settings.
 *
 * This function constructs a Dataset object from raw dataset data and attributes, applying
 * default configurations and determining visibility based on whether it's an analysis result
 * and the spatial aggregation type.
 *
 * @param {Object} params - The parameters object
 * @param {DatasetData} params.data - The raw dataset data containing id, name, spatial type, and rows
 * @param {DatasetAttributeSchema[]} params.attributes - Array of dataset attribute schemas
 * @param {boolean} [params.isAnalysisResult=false] - Whether this dataset is from an analysis result
 *
 * @returns {Dataset} A configured Dataset object with:
 *   - data: Basic dataset information (id, name, spatial_aggregation_type, total_rows)
 *   - properties: Visibility and fetching state based on analysis result and spatial type
 *   - style: Visibility styling ("visible" or "none") based on spatial aggregation type
 *   - categories: The provided attributes array
 *   - selectedCategory: Default selected category from attributes
 *
 * @example
 * ```typescript
 * const dataset = createDefaultSpatialDataset({
 *   data: {
 *     dataset_id: "123",
 *     name: "Population Data",
 *     spatial_aggregation_type: EOutputAnalysisType.GRID,
 *     rows: [...]
 *   },
 *   attributes: [...],
 *   isAnalysisResult: true
 * });
 * ```
 */
export const createDefaultSpatialDataset = ({
  data,
  attributes,
  isAnalysisResult = false
}: {
  data: DatasetData;
  attributes: DatasetAttributeSchema[];
  isAnalysisResult?: boolean;
}): Dataset => ({
  data: {
    ...defaultDataset.data,
    id: data.dataset_id,
    name: data.name,
    display_name: data.display_name ?? data.name,
    spatial_aggregation_type: data.spatial_aggregation_type,
    total_rows: data.rows.length,
    short_description: "",
    dataset_size_kb: data.dataset_size_kb
  },
  properties: {
    isVisible: isAnalysisResult
      ? [
          EOutputAnalysisType.H3,
          EOutputAnalysisType.GEOHASH,
          EOutputAnalysisType.GRID,
          EOutputAnalysisType.PROFILING
        ].includes(data.spatial_aggregation_type as EOutputAnalysisType)
      : false,
    spatialType: data.spatial_aggregation_type,
    isFetching: !isAnalysisResult
  },
  style: {
    ...defaultDataset.style,
    visibility: isAnalysisResult
      ? [
          EOutputAnalysisType.H3,
          EOutputAnalysisType.GEOHASH,
          EOutputAnalysisType.GRID,
          EOutputAnalysisType.PROFILING
        ].includes(data.spatial_aggregation_type as EOutputAnalysisType)
        ? "visible"
        : "none"
      : "none",
    labelConfig: {
      ...defaultDataset.style.labelConfig,
      attribute_id: getDefaultSelectedCategory(attributes).id,
      attribute_name: getDefaultSelectedCategory(attributes).attribute_name
    }
  },
  categories: attributes,
  selectedCategory: getDefaultSelectedCategory(attributes)
});

export const createSpatialDatasetFromMetadata = ({
  data,
  attributes,
  isAnalysisResult = false,
  h3Configuration = null
}: {
  data: DatasetMetadataStream;
  attributes: DatasetAttributeSchema[];
  isAnalysisResult?: boolean;
  h3Configuration?: H3Configuration | null;
}): Dataset => ({
  data: {
    ...defaultDataset.data,
    id: data.dataset_id,
    name: data.name,
    display_name: data.name,
    spatial_aggregation_type: data.spatial_aggregation_type,
    total_rows: data.total_rows,
    short_description: "",
    dataset_size_kb: data.dataset_size_kb
  },
  properties: {
    isVisible: isAnalysisResult
      ? [
          EOutputAnalysisType.H3,
          EOutputAnalysisType.GEOHASH,
          EOutputAnalysisType.GRID,
          EOutputAnalysisType.PROFILING
        ].includes(data.spatial_aggregation_type as EOutputAnalysisType)
      : false,
    spatialType: data.spatial_aggregation_type,
    isFetching: false
  },
  style: {
    ...defaultDataset.style,
    visibility: isAnalysisResult
      ? [
          EOutputAnalysisType.H3,
          EOutputAnalysisType.GEOHASH,
          EOutputAnalysisType.GRID,
          EOutputAnalysisType.PROFILING
        ].includes(data.spatial_aggregation_type as EOutputAnalysisType)
        ? "visible"
        : "none"
      : "none",
    labelConfig: {
      ...defaultDataset.style.labelConfig,
      attribute_id: getDefaultSelectedCategory(attributes).id,
      attribute_name: getDefaultSelectedCategory(attributes).attribute_name
    }
  },
  categories: attributes,
  selectedCategory: getDefaultSelectedCategory(attributes),
  h3Configuration
});

/**
 * Checks if a dataset has a 'score' column and if all of its values are null.
 */
export const hasNoScore = (dataset: DatasetData): boolean => {
  const scoreColumnIndex = dataset.columns.findIndex(
    (column) => column.name.toLowerCase() === "score"
  );

  if (scoreColumnIndex === -1) return false;
  if (!dataset.rows?.length) return true;

  return dataset.rows.every((row) => {
    const value = row[scoreColumnIndex];

    if (value === null || value === undefined) return true;

    if (typeof value === "string") return value.trim().length === 0;

    if (typeof value === "object" && value !== null) {
      return Object.values(value).every((v) => {
        if (v === null || v === undefined) return true;
        if (typeof v === "string") return v.trim().length === 0;
        return false;
      });
    }
    return false;
  });
};

/**
 * Util function to check if a given data type is one of the ordinal types.
 * @param dataType - The data type to check against the ordinal types.
 * @returns {boolean} - Returns true if the data type is one of the ordinal types, false otherwise.
 */
export const isOrdinalType = (
  dataType: DataSchemaType | DatasetDataType
): boolean => {
  return (
    ORDINAL_TYPES.includes(dataType) || ORDINAL_TYPES_COLUMN.includes(dataType)
  );
};

export const generateFilterableAttributes = <T extends DatasetDataColumn>(
  columns: T[] | undefined | null
) => {
  if (!columns || columns.length === 0) {
    return [];
  }

  return columns.map((col) => ({
    label: col.name,
    value: normalizeFilterValue(col.name),
    type: col.data_type
  }));
};

export const getReferenceIdByRowIndex = (
  data: DatasetDetailRow[],
  rowId: string[],
  metadata?: DatasetMetadata
) => {
  if (!metadata) return [];
  const referenceIdIndex = metadata?.schema?.findIndex(
    (d) => d.is_reference_id
  );
  return rowId
    .map((id) => {
      const row = data[Number(id)];
      if (row && referenceIdIndex !== undefined && referenceIdIndex >= 0) {
        return String(
          row[metadata?.schema[referenceIdIndex]?.attribute_name ?? ""]
        );
      }
      return null;
    })
    .filter(Boolean) as string[];
};

export const formatAddAndUpdateDatasetRow = (
  data: Record<string, unknown>,
  metadata?: DatasetMetadata
): AddUpdateRowsDatasetPayload[] => {
  if (!metadata) return [];

  const allowedNames = new Set(metadata.schema.map((m) => m.attribute_name));

  const mappedData = Object.entries(data).map(([key, value]) => {
    const schema = metadata.schema.find((s) => s.attribute_name === key);

    // Handle date/time types
    if (
      schema?.data_type === DataSchemaType.DATE ||
      schema?.data_type === DataSchemaType.DATE_TIME ||
      schema?.data_type === DataSchemaType.TIME
    ) {
      return {
        name: key,
        value: convertFormatterDayjs(
          value as string,
          schema?.time_format || "",
          schema.data_type
        )
      };
    }

    // Handle geometry types
    if (schema?.data_type === DataSchemaType.GEOMETRY) {
      const format = data[`format_geom_${key}`];

      if (format === "coordinates") {
        const latitude = data[`latitude_geom_input_${key}`];
        const longitude = data[`longitude_geom_input_${key}`];
        return {
          name: key,
          value: `POINT(${longitude} ${latitude})`
        };
      }

      if (format === "wkt") {
        return {
          name: key,
          value: data[`wkt_geom_input_${key}`] as string
        };
      }

      if (format === "geojson") {
        return {
          name: key,
          value: data[`geojson_geom_input_${key}`] as string
        };
      }

      if (format === "draw_polygon") {
        return {
          name: key,
          value: data[`draw_polygon_${key}`] as string
        };
      }

      return {
        name: key,
        value: value as string
      };
    }

    // Default (stringify all other types)
    return {
      name: key,
      value: String(value)
    };
  });

  return mappedData.filter((item) => allowedNames.has(item.name));
};

/**
 * Creates dataset attributes from column data
 * @param columns - Array of column data from dataset
 * @returns Array of DatasetAttributeSchema objects
 */
export const createDatasetAttributes = (
  columns: DatasetData["columns"]
): DatasetAttributeSchema[] => {
  return columns.map((column, index) => ({
    ...DATASET_ATTRIBUTE_SCHEMA_DEFAULT_VALUE,
    id: index.toString(),
    attribute_name: column.name,
    data_type: DATA_TYPE_VALUES[column.data_type],
    is_nullable: false,
    is_reference_geo: false,
    is_reference_id: false,
    is_spatial: false,
    position: index
  }));
};

export const createNewDataset = ({
  dataset,
  datasetId,
  datasetName,
  attributesSchema,
  h3Configuration = null,
  styleConfig
}: CreateNewDatasetType): Dataset => {
  return {
    data: {
      ...dataset,
      id: datasetId,
      name: dataset.name,
      display_name: datasetName,
      schema: dataset.schema,
      available: dataset.available ?? true
    },
    properties: {
      isVisible: dataset.available ?? true,
      spatialType: dataset.spatial_aggregation_type
    },
    style: {
      weight: 1,
      opacity: 0.7,
      color: "#fff",
      fillColor: COLORS[styleConfig?.colorFamily ?? "TEAL"][500],
      colorFamily: styleConfig?.colorFamily ?? EColorNames.TEAL,
      fillOpacity: styleConfig?.opacity ?? 0.7,
      visibility: "visible",
      colorRanges: [],
      labelConfig: {
        color_scale:
          styleConfig?.labelConfig?.color_scale ?? EColorLabel.BLACK_WHITE,
        attribute_id:
          styleConfig?.labelConfig?.attribute_id ??
          getDefaultSelectedCategory(attributesSchema)?.id,
        attribute_name:
          attributesSchema.find(
            (item) => item.id === styleConfig?.labelConfig?.attribute_id
          )?.attribute_name ??
          getDefaultSelectedCategory(attributesSchema)?.attribute_name,
        show_label: styleConfig?.labelConfig?.show_label ?? false,
        opacity: styleConfig?.labelConfig?.opacity ?? 0.7
      },
      rankConfig: {
        isEnabled: styleConfig?.rankConfig?.isEnabled ?? false,
        maxRanking: styleConfig?.rankConfig?.maxRanking ?? 10
      },
      stroke: {
        attribute_id:
          styleConfig?.strokeConfig?.attribute_id ??
          getDefaultSelectedCategory(attributesSchema)?.id,
        attribute_name:
          styleConfig?.strokeConfig?.attribute_name ??
          getDefaultSelectedCategory(attributesSchema)?.attribute_name,
        width: styleConfig?.strokeConfig?.width ?? 1,
        opacity: styleConfig?.strokeConfig?.opacity ?? 0.7
      },
      colorSteps: styleConfig?.colorSteps ?? DEFAULT_STEP
    },
    categories: attributesSchema,
    selectedCategory:
      styleConfig?.selectedCategory ??
      getDefaultSelectedCategory(attributesSchema),
    h3Configuration
  };
};

export function buildGridConfig(h3Configuration?: H3Configuration | null) {
  if (!h3Configuration) return {};

  return {
    grid_config: {
      level: h3Configuration.resolution
        ? Number(h3Configuration.resolution)
        : undefined,
      aggregation_type: h3Configuration.aggregationType
    }
  };
}
